import '../Styles/App.css';
import React, { useState, useEffect } from 'react';

import {
  Typography,
  Box,
  Button,
  Container,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useScrollTrigger,
  Grid,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

import { Link as RouterLink } from 'react-router-dom';

import AboutMe1 from '../Assets/Images/About Me (1).jpeg';
import RouteSetting1 from '../Assets/Images/About Me (2).jpeg';
import RouteSetting2 from '../Assets/Images/AboutMe (3).jpeg';

import Footer from '../Components/Layout/Footer';

const imageCards = [
  {
    src: AboutMe1,
    alt: 'Riley climbing indoors',
    height: 420,
  },
  {
    src: RouteSetting1,
    alt: 'Riley routesetting on the wall',
    height: 280,
  },
  {
    src: RouteSetting2,
    alt: 'Riley projecting a climb',
    height: 320,
  },
];

function ImageCard({ src, alt, title, description, height }) {
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '20px',
        height: height,
        border: '1px solid rgba(255,255,255,0.08)',
        backgroundColor: '#18171d',
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          filter: 'brightness(0.85)',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(to top, rgba(10,10,12,0.9) 0%, rgba(10,10,12,0.2) 60%, rgba(10,10,12,0) 100%)',
        }}
      />

      {(title || description) && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            p: 2.5,
          }}
        >
          {title && (
            <Typography sx={{ color: 'text.main', fontWeight: 600, mb: 0.5 }}>
              {title}
            </Typography>
          )}

          {description && (
            <Typography sx={{ color: 'text.alt', fontSize: '0.95rem' }}>
              {description}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

function AboutMe() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  function ElevationScroll({ children }) {
    const trigger = useScrollTrigger({
      disableHysteresis: true,
      threshold: 0,
    });

    return React.cloneElement(children, {
      elevation: trigger ? 6 : 0,
    });
  }

  const menuItems = [
    { label: 'Home', to: '/' },
  ];

  const navButtonSx = {
    typography: 'h6',
    textTransform: 'none',
    fontWeight: 500,
    letterSpacing: '0.4px',
    minWidth: 'auto',
    px: 1.5,
    position: 'relative',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.06)',
      color: 'text.main',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 12,
      right: 12,
      bottom: 6,
      height: '2px',
      backgroundColor: 'currentColor',
      transform: 'scaleX(0)',
      transformOrigin: 'center',
      transition: 'transform 0.28s ease',
    },
    '&:hover::after': {
      transform: 'scaleX(1)',
    },
  };

  return (
    <main style={{ width: '100%', overflowX: 'hidden' }}>
      {/* App Bar */}
      <Container maxWidth="xl" disableGutters sx={{ overflowX: 'hidden' }}>
        <ElevationScroll>
          <AppBar
            className="appbar"
            elevation={0}
            sx={{
              bgcolor: 'background.main',
              width: '100%',
              left: 0,
              right: 0,
            }}
          >
            <Toolbar
              sx={{
                justifyContent: 'flex-start',
                marginBlock: 1,
                px: { xs: 2, sm: 4, lg: 10 },
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              {/* Mobile Menu */}
              <IconButton
                color="inherit"
                aria-label="open mobile menu"
                edge="end"
                onClick={toggleMobileMenu}
                sx={{ display: { lg: 'none' } }}
              >
                <MenuIcon />
              </IconButton>

              {/* Desktop Menu */}
              <Box
                sx={{
                  display: { xs: 'none', lg: 'flex' },
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {menuItems.map((item, index) => (
                  <Button
                    key={index}
                    component={RouterLink}
                    to={item.to}
                    onClick={() => {
                      if (item.label === 'About Me') {
                        window.scrollTo({ top: 0, behavior: 'instant' });
                      }
                    }}
                    sx={{
                      ...navButtonSx,
                      backgroundColor:
                        item.label === 'About Me'
                          ? 'rgba(255,255,255,0.08)'
                          : 'transparent',
                      color:
                        item.label === 'About Me'
                          ? 'text.main'
                          : 'text.main',
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            </Toolbar>
          </AppBar>
        </ElevationScroll>
      </Container>

      {/* Mobile Drawer Menu */}
      <Drawer anchor="top" open={isMobileMenuOpen} onClose={closeMobileMenu}>
        <List sx={{ bgcolor: 'background.main' }}>
          {menuItems.map((item, index) => (
            <ListItem
              key={index}
              button
              component={RouterLink}
              to={item.to}
              onClick={() => {
                closeMobileMenu();

                if (item.label === 'About Me') {
                  window.scrollTo({ top: 0, behavior: 'instant' });
                }
              }}
              sx={{
                borderColor: 'background.main',
                bgcolor:
                  item.label === 'About Me'
                    ? 'rgba(255,255,255,0.08)'
                    : 'background.alt',
                border: 2,
                boxSizing: 'border-box',
              }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  sx: {
                    color:
                      item.label === 'About Me'
                        ? 'text.main'
                        : 'text.link',
                    fontWeight: 500,
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Drawer>

      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#121116',
          pt: { xs: 12, md: 16 },
          pb: { xs: 10, md: 14 },
        }}
      >
        <Container maxWidth="lg">
          {/* Hero Section */}
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  color: 'text.link',
                  fontWeight: 600,
                  mb: 2,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                About Me
              </Typography>

              <Typography
                variant="h2"
                sx={{
                  color: 'text.main',
                  fontWeight: 800,
                  mb: 3,
                }}
              >
                Climber, route setter,
                <br />
                and developer.
              </Typography>

              <Typography sx={{ color: 'text.alt', lineHeight: 1.9 }}>
                My approach to development is heavily influenced by climbing.
                Both involve solving problems, testing ideas, and refining until
                something feels right. Whether I am building a mechanic or
                climbing a route, I am constantly thinking about flow,
                difficulty, and how the experience feels to move through.
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={7}>
                  <ImageCard {...imageCards[0]} />
                </Grid>

                <Grid item xs={12} sm={5}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <ImageCard {...imageCards[1]} />
                    <ImageCard {...imageCards[2]} />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Divider sx={{ my: 8, borderColor: 'rgba(255,255,255,0.08)' }} />

          {/* Explanation Section */}
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" sx={{ color: 'text.main', mb: 2 }}>
                Climbing ↔ Development
              </Typography>

              <Typography sx={{ color: 'text.alt', lineHeight: 1.9, mb: 2 }}>
                Climbing teaches you to break down complex problems into smaller,
                readable steps. You test different solutions, fail often, and
                adjust based on feedback. That exact same loop exists in
                development.
              </Typography>

              <Typography sx={{ color: 'text.alt', lineHeight: 1.9 }}>
                The goal in both is not just difficulty, but clarity. The best
                systems and the best climbs feel challenging while still being
                understandable.
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: '20px',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <Typography sx={{ color: 'text.link', fontWeight: 600, mb: 2 }}>
                  Routesetting Principles
                </Typography>

                <Box sx={{ display: 'grid', gap: 2 }}>
                  {[
                    'Designing intentional movement',
                    'Balancing challenge and readability',
                    'Testing and iterating constantly',
                    'Creating memorable experiences',
                  ].map((text) => (
                    <Typography key={text} sx={{ color: 'text.alt' }}>
                      • {text}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 8, maxWidth: 800 }}>
            <Typography variant="h4" sx={{ color: 'text.main', mb: 2 }}>
              What I bring
            </Typography>

            <Typography sx={{ color: 'text.alt', lineHeight: 1.9 }}>
              Climbing has shaped how I approach creative work. It has taught me
              patience, attention to detail, and the importance of iteration.
              Routesetting pushed that further by making me design experiences
              for others, not just solve problems myself. That mindset carries
              directly into the games I build.
            </Typography>
          </Box>
        </Container>
      </Box>

      <Footer />
    </main>
  );
}

export default AboutMe;